// src/stores/useMedicalRequestStore.ts
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { PatientDetails, ProxyDetails, RequestReporterType } from '../types';
import { submitMedicalRequestApi } from '../services/medical-request.service';
import { useVerifyPatientDetails } from './useVerifyPatientDetails';
import { useVerifyNhsLogin } from './useVerifyNhsLogin';
import { useApiLoading } from '../lib/composables/useApiLoading';
import { useAttachments } from '../lib/composables/useAttachments';
import { useSubmitAction } from '../lib/composables/useSubmitAction';
import { usePatientIdentity } from '../lib/composables/usePatientIdentity';

export const useMedicalRequestStore = defineStore('medical-request', () => {
  const expiryTime = ref<Date | null>(null); // +1 hour

  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  const isEmergency = ref<boolean>(false);
  const reporterType = ref<RequestReporterType | null>(null);
  // unique after here, above can be made modular and shared

  const proxyDetails = ref<ProxyDetails | null>();
  const {
    patientDetails,
    isValidPatient,
    isVerified,
    isNhsVerified,
    setPatientDetails,
    setIsValidPatient,
    setIsNhsVerified,
    resetIdentity,
  } = usePatientIdentity();

  const problemDescription = ref<string>('');
  const problemRecentContact = ref<string>('');

  const { attachments, addAttachment, addAttachments, removeAttachment, clearAttachments } =
    useAttachments({ max: 10 });

  const isExpired = computed(
    () => expiryTime.value !== null && Date.now() > expiryTime.value.getTime(),
  );
  const isProxy = computed(() => reporterType.value === 'PROXY');

  function startNew() {
    clearAttachments();
    expiryTime.value = new Date(Date.now() + 60 * 60 * 1000); // +1 hour
    isEmergency.value = false;
    resetIdentity();
    problemDescription.value = '';
    problemRecentContact.value = '';
    proxyDetails.value = null;
    reporterType.value = null;
  }

  function endCurrent() {
    startNew();
    expiryTime.value = null;
  }

  function setIsEmergency(value: boolean) {
    isEmergency.value = value;
  }

  function setReporterType(value: RequestReporterType) {
    reporterType.value = value;
  }

  function setProxyDetails(details: ProxyDetails) {
    proxyDetails.value = details;
  }

  const verifyPatientDetails = useVerifyPatientDetails({
    patientDetails,
    isProxy: isProxy,
    setApiLoading,
    setApiDone,
    setIsValidPatient,
  });

  const verifyNhsLogin = async (code: string, csrfToken: string) => {
    const run = useVerifyNhsLogin({
      code,
      csrfToken,
      setApiLoading,
      setApiDone,
      setIsValidPatient,
      setIsNhsVerified,
      setPatientDetails,
    });

    const result = await run();
    if (result.ok) {
      setReporterType('PATIENT');
    }
    return result;
  };

  const submitAction = () =>
    useSubmitAction({ setApiLoading, setApiDone, endCurrent })(() =>
      submitMedicalRequestApi({
        reporterType: reporterType.value,
        proxyDetails: proxyDetails.value,
        patientDetails: patientDetails.value,
        problemDescription: problemDescription.value,
        problemRecentContact: problemRecentContact.value,
        attachments: attachments.value,
      }),
    );

  function setProblemDescription(text: string) {
    problemDescription.value = text;
  }

  function setProblemRecentContact(text: string) {
    problemRecentContact.value = text;
  }

  function setNhsSsoDetails(patientDetails: PatientDetails) {
    // NB. only patients verified via NHS App use this.
    setIsNhsVerified(true);
    // like this - setIsNhsApp(true);
    setPatientDetails(patientDetails);
    setIsValidPatient(true);
    setReporterType('PATIENT');
  }

  // ---- Expose flat state ----
  return {
    // apiLoading,
    apiLoading,
    // state
    expiryTime,

    //
    isEmergency,
    reporterType,
    proxyDetails,
    patientDetails,
    isProxy,
    isValidPatient,
    isVerified,
    isNhsVerified,
    problemDescription,
    problemRecentContact,
    attachments,

    // getters
    isExpired,

    // actions
    addAttachment,
    addAttachments,
    endCurrent,
    removeAttachment,
    setProblemDescription,
    setIsEmergency,
    setPatientDetails,
    setProxyDetails,
    setProblemRecentContact,
    setReporterType,
    setNhsSsoDetails: setNhsSsoDetails,
    startNew,
    submitAction,
    //
    verifyPatientDetails,
    verifyNhsLogin,
  };
});
